/**
 * AlertModal — Modal d'alerte (remplace window.alert) et de confirmation (remplace window.confirm).
 *
 * Règle DA immuable §12 terdecies : aucun composant natif du navigateur.
 * Cf. memory/feedback_no_native_browser_dialogs.md
 *
 * Props :
 *   - open: bool
 *   - onClose: () => void                   (croix ✕ + clic fond — ferme sans side-effect §12 sexies)
 *   - onConfirm?: () => void                (bouton principal ; défaut = onClose si non fourni)
 *   - variant?: 'info'|'success'|'error'  (défaut 'info')
 *   - title?: string                       (défaut auto selon variant)
 *   - message: string | ReactNode
 *   - confirmLabel?: string                (défaut 'OK')
 *   - cancelLabel?: string                 (si fourni → mode confirmation à 2 boutons : Cancel + Confirm)
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import styled, { keyframes } from 'styled-components';
import {COLORS, FONTS, RADII, SHADOW, media, FONT_SIZE, FONT_WEIGHT} from '../../components/ui/theme';
import { PrimaryButton, GhostButton } from '../../components/ui/daComponents';

const fadeIn = keyframes`from{opacity:0}to{opacity:1}`;
const slideUp = keyframes`from{transform:translateY(20px);opacity:0}to{transform:translateY(0);opacity:1}`;

const Overlay = styled.div`
  position: fixed; inset: 0; background: rgba(13,10,16,.55);
  display: flex; align-items: center; justify-content: center;
  z-index: 1050; padding: 1rem;
  animation: ${fadeIn} .18s ease-out;
`;
const Card = styled.div`
  background: ${COLORS.white}; border-radius: ${RADII.card};
  width: 100%; max-width: 24rem;
  box-shadow: ${SHADOW.hover};
  overflow: hidden;
  animation: ${slideUp} .25s ease-out;
  font-family: ${FONTS.body}; color: ${COLORS.ink};
`;
const headerColor = (variant) =>
  variant === 'error' ? COLORS.danger
  : variant === 'success' ? COLORS.cta
  : COLORS.accent;
const Header = styled.div`
  background: ${(p) => headerColor(p.$variant)}; color: #fff;
  padding: 1.1rem 1.5rem;
  display: flex; justify-content: space-between; align-items: center;
`;
const HeaderTitle = styled.div`font-family: ${FONTS.head}; font-weight: ${FONT_WEIGHT.extrabold}; font-size: 1.05rem;`;
const CloseBtn = styled.button`
  width: 2rem; height: 2rem; border-radius: 50%;
  background: rgba(255,255,255,.2); color: #fff;
  border: none; cursor: pointer; font-size: ${FONT_SIZE.base};
  display: flex; align-items: center; justify-content: center;
  &:hover { background: rgba(255,255,255,.3); }
`;
const Body = styled.div`
  padding: 1.5rem; font-size: .95rem; line-height: 1.5; color: ${COLORS.ink};
  ${media.sm`padding: 1.1rem 1rem; font-size: .9rem;`}
`;
const Footer = styled.div`
  padding: 1rem 1.5rem;
  display: flex; justify-content: flex-end; gap: .6rem;
  border-top: .0625rem solid ${COLORS.line};
  ${media.sm`padding: .85rem 1rem;`}
`;

const defaultTitleKeys = { info: 'AlertTitleInformation', success: 'AlertTitleSuccess', error: 'AlertTitleError' };

function AlertModal({ open, onClose, onConfirm = null, variant = 'info', title, message, confirmLabel, cancelLabel = null }) {
  const { t } = useTranslation();
  if (!open) return null;
  const resolvedTitle = title || t(defaultTitleKeys[variant] || defaultTitleKeys.info);
  const resolvedConfirmLabel = confirmLabel || t('OK');
  // onConfirm = action du bouton principal. Si absent, fallback sur onClose (rétrocompat).
  const handleConfirm = onConfirm || onClose;
  return (
    <Overlay onClick={onClose}>
      <Card onClick={(e) => e.stopPropagation()}>
        <Header $variant={variant}>
          <HeaderTitle>{resolvedTitle}</HeaderTitle>
          <CloseBtn onClick={onClose} aria-label={t('Close')}>✕</CloseBtn>
        </Header>
        <Body>{message}</Body>
        <Footer>
          {/* cancelLabel fourni → mode confirm à 2 boutons (remplace window.confirm) */}
          {cancelLabel && <GhostButton onClick={onClose}>{cancelLabel}</GhostButton>}
          <PrimaryButton onClick={handleConfirm}>{resolvedConfirmLabel}</PrimaryButton>
        </Footer>
      </Card>
    </Overlay>
  );
}

export default AlertModal;
